import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getNotificationsApi,
  markNotificationAsReadApi,
  markAllNotificationsAsReadApi,
} from '../../api/notifications';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params = { page: 0, limit: 15 }, { rejectWithValue }) => {
    try {
      const { page, limit } = params;
      return await getNotificationsApi(page, limit);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch notifications' }
      );
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markNotificationAsRead',
  async (id, { rejectWithValue }) => {
    try {
      const isValidObjectId = typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);
      if (!isValidObjectId) {
        console.log(`Local update for socket/non-persisted notification ID: ${id}`);
        return id;
      }
      await markNotificationAsReadApi(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to mark notification as read' }
      );
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllNotificationsAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await markAllNotificationsAsReadApi();
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to mark all notifications as read' }
      );
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    notifications: [],
    unreadCount: 0,
    pagination: {
      total: 0,
      page: 0,
      limit: 15,
      pages: 0,
      hasNextPage: false,
    },
    loading: false,
    error: null,
  },
  reducers: {
    addLiveNotification: (state, action) => {
      // Ensure we don't add duplicate notifications
      const id = action.payload._id || action.payload.id;
      const exists = state.notifications.some((n) => (n._id || n.id) === id);
      if (!exists) {
        state.notifications.unshift(action.payload);
        const isUnread = !(action.payload.read || action.payload.isRead);
        if (isUnread) {
          state.unreadCount += 1;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchNotifications ──
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        
        // Robust checking for data arrays
        let items = [];
        if (Array.isArray(payload)) {
          items = payload;
        } else if (Array.isArray(payload?.data)) {
          items = payload.data;
        } else if (Array.isArray(payload?.data?.notifications)) {
          items = payload.data.notifications;
        } else if (Array.isArray(payload?.notifications)) {
          items = payload.notifications;
        }
        
        state.notifications = items;
        
        // Set unread count based on API returned metadata, fallback to counting manually
        if (typeof payload?.notifications?.unread === 'number') {
          state.unreadCount = payload.notifications.unread;
        } else {
          state.unreadCount = items.filter((n) => !(n.read || n.isRead)).length;
        }

        // Set pagination state from payload if present
        if (payload?.pagination) {
          state.pagination = {
            total: payload.pagination.totalRecords || payload.pagination.total || 0,
            page: typeof payload.pagination.currentPage === 'number'
              ? Math.max(0, payload.pagination.currentPage - 1) // convert 1-based index from backend to 0-based index
              : state.pagination.page,
            limit: payload.pagination.perPage || payload.pagination.limit || 15,
            pages: payload.pagination.totalPages || payload.pagination.pages || 0,
            hasNextPage: payload.pagination.hasNextPage || false,
          };
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch notifications';
      })
      
      // ── markNotificationAsRead ──
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const id = action.payload;
        const index = state.notifications.findIndex((n) => (n._id || n.id) === id);
        if (index !== -1) {
          const wasUnread = !(state.notifications[index].read || state.notifications[index].isRead);
          state.notifications[index].read = true;
          state.notifications[index].isRead = true;
          if (wasUnread && state.unreadCount > 0) {
            state.unreadCount -= 1;
          }
        }
      })
      
      // ── markAllNotificationsAsRead ──
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((n) => ({
          ...n,
          read: true,
          isRead: true,
        }));
        state.unreadCount = 0;
      });
  },
});

export const { addLiveNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
